import { Toaster } from "@client/components/ui/sonner";
import { useReferral } from "@client/hooks/use-referral";
import { useAuthCacheGuard } from "@client/hooks/useAuthCacheGuard";
import { ThemeProvider } from "@client/lib/theme-provider";
import type { trpc } from "@client/lib/trpc-client";
import type { QueryClient } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import {
	createRootRouteWithContext,
	HeadContent,
	Outlet,
	useMatches,
} from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import "../index.css";
import { AppFooter } from "@client/components/app-footer";
import { Header } from "@client/components/navbar/header";
import { NotFoundPage } from "@client/components/not-found-page";

export interface RouterAppContext {
	trpc: typeof trpc;
	queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterAppContext>()({
	component: RootComponent,
	notFoundComponent: NotFoundPage,
	head: () => ({
		meta: [
			{
				title: "LLM Generator",
			},
			{
				name: "description",
				content:
					"LLM Generator is a starter kit for full-stack React Vite apps deployed to Cloudflare Workers",
			},
		],
	}),
});

function RootComponent() {
	useReferral();
	// Never serve one account's cached data to the next one
	useAuthCacheGuard();
	const matches = useMatches();

	// Check if we're on an auth route (routes under the (auth) group or the root landing page)
	const isAuthRoute = matches.some(
		(match) => match.routeId.includes("/(auth)/") || match.routeId === "/",
	);

	return (
		<>
			<HeadContent />
			<ThemeProvider defaultTheme="dark" storageKey="vite-ui-theme">
				<div className="relative grid min-h-screen grid-rows-[auto_1fr_auto] bg-background">
					{!isAuthRoute && <Header />}
					<main className="relative">
						<Outlet />
					</main>
					{!isAuthRoute && <AppFooter />}
				</div>
				<Toaster richColors position="top-center" />
			</ThemeProvider>
			<TanStackRouterDevtools position="bottom-left" />
			<ReactQueryDevtools position="bottom" buttonPosition="bottom-right" />
		</>
	);
}
